import * as jsPDF from 'jspdf';

import { DOCUMENT, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, inject, viewChild } from '@angular/core';

import { WidgetConfiguration } from '@unified-ui/shared-components';
import { GridStackOptions } from 'gridstack';
import { GridstackModule } from 'gridstack/dist/angular';
import html2canvas from 'html2canvas';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { DashboardWidgetComponent } from '../dashboard-widget/dashboard-widget.component';

@Component({
    selector: 'unified-ui-dashboard-framework',
    standalone: true,
    imports: [NgClass, GridstackModule, ButtonModule, CardModule, ToolbarModule, TooltipModule, DashboardWidgetComponent],
    templateUrl: './dashboard-framework.component.html',
    styleUrls: ['./dashboard-framework.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardFrameworkComponent {
    private document = inject<any>(DOCUMENT);

    public readonly card = viewChild.required<any>('card');

    // Click on edit button
    public editClick = output();

    public gridStackOptions: GridStackOptions = {
        margin: 4,
        minRow: 6,
        maxRow: 6,
        animate: true,
        staticGrid: true
    };
    public isFullScreen = false;
    public isNotReflow = true;
    public layout = input<DashboardLayout>();

    protected widgets = signal<WidgetConfiguration[]>([]);

    protected currentLayout = computed(() => {
        this.gridStackOptions.staticGrid = this.layout()?.staticGrid;
        return this.layout();
    });

    constructor() {
        effect(() => {
            this.widgets.set(this.layout()?.widgets ?? []);
        });
    }

    protected export(): void {
        const data = document.getElementById('content');
        html2canvas(data!).then(canvas => {
            const imgWidth = 296;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const contentDataURL = canvas.toDataURL('image/svg+xml;base64');
            const pdf = new jsPDF.jsPDF('l', 'mm', 'a4'); // A4 size page of PDF
            // const pdf = new jsPDF.jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' }); // A4 size page of PDF
            pdf.addImage(contentDataURL, 'PNG', 0, 0, imgWidth, imgHeight);
            pdf.save('exported-file.pdf'); // Save the generated PDF
        });
    }

    protected reflow(): void {
        this.isNotReflow = !this.isNotReflow;
        this.gridStackOptions.staticGrid = !this.gridStackOptions.staticGrid;
        // this.gridStackOptions.disableDrag = false;
    }

    protected setFullScreen(): void {
        this.isFullScreen = !this.isFullScreen;
        this.isFullScreen ? this.openFullscreen() : this.closeFullscreen();
    }

    private closeFullscreen(): void {
        if (this.document.exitFullscreen) {
            this.document.exitFullscreen();
        } else if (this.document.mozCancelFullScreen) {
            /* Firefox */
            this.document.mozCancelFullScreen();
        } else if (this.document.webkitExitFullscreen) {
            /* Chrome, Safari and Opera */
            this.document.webkitExitFullscreen();
        } else if (this.document.msExitFullscreen) {
            /* IE/Edge */
            this.document.msExitFullscreen();
        }
    }

    private openFullscreen(): void {
        const card = this.card();
        if (card.el.nativeElement.requestFullscreen) {
            card.el.nativeElement.requestFullscreen().catch((err: any) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else if (card.el.nativeElement.mozRequestFullScreen) {
            /* Firefox */
            card.el.nativeElement.mozRequestFullScreen().catch((err: any) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else if (card.el.nativeElement.webkitRequestFullscreen) {
            /* Chrome, Safari and Opera */
            card.el.nativeElement.webkitRequestFullscreen().catch((err: any) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else if (card.el.nativeElement.msRequestFullscreen) {
            /* IE/Edge */
            card.el.nativeElement.msRequestFullscreen().catch((err: any) => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        }
    }
}

// FIXME: Should be in a separate types file.
export type DashboardLayout = {
    // changes the number of columns for the dashboard. Default is 12
    column?: number;

    // If set to true, allows the widgets to flow rather than grow/shrink with the resizing of of the dashboard
    reflow?: Boolean;

    // When statis, no items can be moved or rearranged on the dashboard
    staticGrid?: boolean;

    //Title of the dashboard
    title?: string;

    // Array of widget configurations
    widgets?: WidgetConfiguration[];
};
